import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLeading,
} from 'redux-saga/effects';

import * as Types from '../actions';
import {
  loginFailed,
  loginSuccess,
  logoutFailure,
  logoutSuccess,
} from '../actionCreators';

function stringifyError(data: any): string {
  if (data.detail) {
    const detail =
      typeof data.detail === 'string' ? JSON.parse(data.detail) : data.detail;
    if (Array.isArray(detail)) {
      return detail.join('');
    } else if (typeof detail === 'object') {
      return Object.keys(detail)
        .map((key) =>
          Array.isArray(detail[key])
            ? `${key} ${detail[key].join('')}`
            : `${key} ${detail[key]}`
        )
        .join(' ')
        .replace(/errors\s?/, '');
    } else {
      return detail;
    }
  } else {
    if (data.message) {
      const message =
        typeof data.message === 'string'
          ? JSON.parse(data.message)
          : data.message;
      if (Array.isArray(message)) {
        return message.join('');
      } else if (typeof message === 'object') {
        return Object.keys(message)
          .map((key) =>
            Array.isArray(message[key])
              ? `${key} ${message[key].join('')}`
              : `${key} ${message[key]}`
          )
          .join(' ')
          .replace(/errors\s?/, '');
      } else {
        return message;
      }
    } else {
      return JSON.stringify(data);
    }
  }
}

type QBLoginParams = { login: string; password: string };
type QBLoginResult = { session: any; user: any };

function QBLogin(params: QBLoginParams): Promise<QBLoginResult> {
  return new Promise((resolve, reject) => {
    QB.createSession((sessionError: any, session: any) => {
      if (sessionError) {
        return reject(stringifyError(sessionError));
      }
      QB.login(params, (loginError: any, user: any) => {
        if (loginError) {
          QB.users.create(
            { ...params, full_name: params.login },
            (createUserError: any) => {
              if (createUserError) {
                return reject(stringifyError(createUserError));
              }
              QB.login(params, (loginError2: any, user2: any) => {
                if (loginError2) {
                  return reject(stringifyError(loginError2));
                }
                return resolve({ session, user: user2 });
              });
            }
          );
        } else {
          return resolve({ session, user });
        }
      });
    });
  });
}

function QBLogout(): Promise<void> {
  return new Promise((resolve, reject) => {
    QB.logout((logoutErr: any) => {
      if (logoutErr) {
        return reject(stringifyError(logoutErr));
      }
      QB.destroySession((sessionError: any) => {
        if (sessionError) {
          return reject(stringifyError(sessionError));
        }
        return resolve();
      });
    });
  });
}

export function* login(
  action: Types.LoginRequestAction
): Generator<
  | CallEffect<QBLoginResult>
  | PutEffect<Types.LoginSuccessAction>
  | PutEffect<Types.LoginFailureAction>
> {
  const { login, password, then } = action.payload;
  try {
    const response = (yield call(QBLogin, {
      login,
      password,
    })) as QBLoginResult;
    const result = loginSuccess(response);
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(loginFailed(e.message || e));
  }
}

export function* logout(
  action: Types.LogoutRequestAction
): Generator<
  | CallEffect<void>
  | PutEffect<Types.LogoutSuccessAction>
  | PutEffect<Types.LogoutFailureAction>
> {
  try {
    const { then } = action.payload;
    yield call(QBLogout);
    const result = logoutSuccess();
    yield put(result);
    then && then(result);
  } catch (e) {
    yield put(logoutFailure(e.message || e));
  }
}

export default [
  takeLeading(Types.LOGIN_REQUEST, login),
  takeLeading(Types.LOGOUT_REQUEST, logout),
];
